/**
 * Componente: RegisterForm
 * Formulário de registro com React hooks e validação Zod
 */

import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserRole } from '@/types/auth';
import { useAuth } from '@/hooks/useAuth';
import { BRAZIL_UF_OPTIONS, registerSchema } from '@/lib/validations';
import { evaluatePasswordStrength } from '@/lib/passwordPolicy';
import { PasswordRequirementsChecklist } from '@/components/auth/PasswordRequirementsChecklist';
import { translateAuthError } from '@/lib/errorTranslations';
import { getAuthToken } from '@/utils/auth';
import { resolvePostRegisterRedirect } from '@/lib/postAuthSession';
import { stripMask } from '@/utils/masks';

type RegisterFormProps = {
  onSuccess?: () => void;
};

const roleOptions = [
  { value: UserRole.PATIENT, label: 'Paciente' },
  { value: UserRole.DOCTOR, label: 'Médico' },
];

export function RegisterForm({ onSuccess }: RegisterFormProps) {
  const navigate = useNavigate();
  const { register } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);

  const [isPending, setIsPending] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.PATIENT);
  const [formDataLocal, setFormDataLocal] = useState({
    password: '',
    confirmPassword: '',
  });
  const [crmValue, setCrmValue] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [displayedError, setDisplayedError] = useState<string>('');

  const handlePasswordChange = (value: string) => {
    setFormDataLocal((prev) => ({ ...prev, password: value }));
    setPasswordStrength(evaluatePasswordStrength(value));
  };

  const handleConfirmPasswordChange = (value: string) => {
    setFormDataLocal((prev) => ({ ...prev, confirmPassword: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsPending(true);
    setDisplayedError('');
    setFieldErrors({});

    try {
      const formData = new FormData(e.currentTarget);
      const email = formData.get('email') as string;
      const name = formData.get('name') as string;
      const password = formData.get('password') as string;
      const confirmPassword = formData.get('confirmPassword') as string;
      const role = (formData.get('role') as UserRole) || UserRole.PATIENT;
      const crm = stripMask(formData.get('crm') as string) || undefined;
      const crmState = (formData.get('crmState') as string) || undefined;

      const validationResult = registerSchema.safeParse({
        email,
        name,
        password,
        confirmPassword,
        role,
        crm,
        crmState,
      });

      if (!validationResult.success) {
        const errors = validationResult.error.flatten().fieldErrors;
        setFieldErrors(errors as Record<string, string[]>);
        setDisplayedError('Por favor, verifique os erros no formulário');
        setIsPending(false);
        return;
      }

      const { confirmPassword: _, ...apiData } = validationResult.data;
      const payload = {
        ...apiData,
        crm: apiData.role === UserRole.DOCTOR ? apiData.crm?.trim() : undefined,
        crmState:
          apiData.role === UserRole.DOCTOR
            ? apiData.crmState?.trim().toUpperCase()
            : undefined,
      };

      const session = await register(payload);

      if (!getAuthToken() || !session?.user?.id) {
        throw new Error('Falha ao estabelecer sessão após o cadastro. Tente fazer login.');
      }

      onSuccess?.();

      navigate(resolvePostRegisterRedirect(session.user.role), { replace: true });
    } catch (error) {
      console.error('Register error:', error);

      let message = 'Erro ao registrar. Tente novamente.';
      if (error instanceof Error) {
        message = error.message;
      }

      const translatedMessage = translateAuthError(message);
      setDisplayedError(translatedMessage);
      setIsPending(false);
    }
  };

  return (
    <div className="w-full">
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        {displayedError && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{displayedError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-gray-700">
            Nome Completo
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Seu nome"
            required
            disabled={isPending}
            className={`transition-colors focus:ring-2 focus:ring-teal-500 ${fieldErrors.name ? 'border-red-500' : 'border-gray-300'}`}
          />
          {fieldErrors.name && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.name?.[0]}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="seu@email.com"
            required
            disabled={isPending}
            className={`transition-colors focus:ring-2 focus:ring-teal-500 ${fieldErrors.email ? 'border-red-500' : 'border-gray-300'}`}
          />
          {fieldErrors.email && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.email?.[0]}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="role" className="text-sm font-medium text-gray-700">
            Qual é seu papel?
          </Label>
          <select
            id="role"
            name="role"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as UserRole)}
            disabled={isPending}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors"
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {selectedRole === UserRole.DOCTOR && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="crm" className="text-sm font-medium text-gray-700">
                CRM *
              </Label>
              <Input
                id="crm"
                name="crm"
                type="text"
                inputMode="numeric"
                placeholder="Ex: 123456"
                required
                disabled={isPending}
                value={crmValue}
                onChange={(e) => setCrmValue(stripMask(e.target.value))}
                className={`transition-colors focus:ring-2 focus:ring-teal-500 ${fieldErrors.crm ? 'border-red-500' : 'border-gray-300'}`}
              />
              {fieldErrors.crm && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.crm?.[0]}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="crmState" className="text-sm font-medium text-gray-700">
                UF *
              </Label>
              <select
                id="crmState"
                name="crmState"
                defaultValue=""
                required
                disabled={isPending}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors ${
                  fieldErrors.crmState ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="" disabled>
                  UF
                </option>
                {BRAZIL_UF_OPTIONS.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
              {fieldErrors.crmState && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.crmState?.[0]}</p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-gray-700">
            Senha
          </Label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Mínimo 8 caracteres"
              required
              disabled={isPending}
              onChange={(e) => handlePasswordChange(e.target.value)}
              className={`pr-12 transition-colors focus:ring-2 focus:ring-teal-500 ${fieldErrors.password ? 'border-red-500' : 'border-gray-300'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 rounded transition-colors"
              disabled={isPending}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
          {fieldErrors.password && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.password?.[0]}</p>
          )}
          {formDataLocal.password && (
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    passwordStrength === 'weak'
                      ? 'w-1/3 bg-red-500'
                      : passwordStrength === 'medium'
                        ? 'w-2/3 bg-yellow-500'
                        : 'w-full bg-teal-500'
                  }`}
                />
              </div>
              <span className="text-xs font-medium text-gray-600">
                {passwordStrength === 'weak' ? 'Fraca' : passwordStrength === 'medium' ? 'Média' : 'Forte'}
              </span>
            </div>
          )}
          <PasswordRequirementsChecklist password={formDataLocal.password} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
            Confirmar Senha
          </Label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            placeholder="Repita sua senha"
            required
            disabled={isPending}
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
            className={`transition-colors focus:ring-2 focus:ring-teal-500 ${fieldErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
          />
          {fieldErrors.confirmPassword && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.confirmPassword?.[0]}</p>
          )}
          {formDataLocal.password && formDataLocal.confirmPassword === formDataLocal.password && (
            <div className="flex items-center gap-2 text-teal-600">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-xs font-medium">Senhas coincidem</span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
        >
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isPending ? 'Criando conta...' : 'Criar Conta'}
        </button>

        <p className="text-sm text-gray-600 text-center">
          Já tem conta?{' '}
          <Link to="/login" className="text-teal-600 hover:text-teal-700 font-semibold">
            Faça login
          </Link>
        </p>
      </form>
    </div>
  );
}
